import { z } from 'zod';
// ---------- Enums ----------
export const InvoiceStatusSchema = z.enum(['BORRADOR', 'EMITIDA', 'PARCIAL', 'COBRADA', 'ANULADA']);
export const InvoiceStatusLabel = {
    BORRADOR: 'Borrador',
    EMITIDA: 'Emitida',
    PARCIAL: 'Cobrada parcial',
    COBRADA: 'Cobrada',
    ANULADA: 'Anulada',
};
export const InvoiceKindSchema = z.enum(['ORDINARIA', 'RECTIFICATIVA', 'PROFORMA']);
export const InvoiceKindLabel = {
    ORDINARIA: 'Ordinaria',
    RECTIFICATIVA: 'Rectificativa',
    PROFORMA: 'Proforma',
};
// F21 — Método de rectificación (art. 15 RD 1619/2012).
export const RectificationTypeSchema = z.enum(['POR_SUSTITUCION', 'POR_DIFERENCIAS']);
export const RectificationTypeLabel = {
    POR_SUSTITUCION: 'Por sustitución',
    POR_DIFERENCIAS: 'Por diferencias',
};
/** Código de rectificativa de la AEAT (VERI*FACTU): R1 error fundado en derecho, R2 concurso, R3 incobrable, R4 resto. */
export const AeatRectificationCodeSchema = z.enum(['R1', 'R2', 'R3', 'R4']);
export const AeatRectificationCodeLabel = {
    R1: 'R1 · Error fundado en derecho o art. 80 Uno, Dos y Seis LIVA',
    R2: 'R2 · Concurso de acreedores (art. 80 Tres LIVA)',
    R3: 'R3 · Crédito incobrable (art. 80 Cuatro LIVA)',
    R4: 'R4 · Resto de causas',
};
export const RectifyInvoiceSchema = z.object({
    reason: z.string().min(3).max(500),
    type: RectificationTypeSchema.default('POR_DIFERENCIAS'),
    /** Código R de la AEAT; solo se usa si la factura se expide con VERI*FACTU (por defecto, R4). */
    aeatType: AeatRectificationCodeSchema.optional(),
});
export const PaymentMethodSchema = z.enum([
    'TRANSFERENCIA',
    'EFECTIVO',
    'TARJETA',
    'CHEQUE',
    'DOMICILIACION',
    'COMPENSACION',
    'OTRO',
]);
export const PaymentMethodLabel = {
    TRANSFERENCIA: 'Transferencia',
    EFECTIVO: 'Efectivo',
    TARJETA: 'Tarjeta',
    CHEQUE: 'Cheque',
    DOMICILIACION: 'Domiciliación',
    COMPENSACION: 'Compensación',
    OTRO: 'Otro',
};
export const FundsProvisionStatusSchema = z.enum(['ACTIVE', 'APPLIED', 'REFUNDED']);
export const FundsProvisionStatusLabel = {
    ACTIVE: 'Activa',
    APPLIED: 'Aplicada',
    REFUNDED: 'Devuelta',
};
// ---------- Líneas ----------
const decimalString = z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'number' ? v.toString() : v));
/**
 * Calificación o causa de exención de una línea (VERI*FACTU). Nula = S1 (sujeta, con IVA) si el tipo
 * es mayor que 0; una línea al 0 % tiene que decir por qué.
 */
export const TaxTreatmentSchema = z.enum([
    'S1',
    'S2',
    'N1',
    'N2',
    'E1',
    'E2',
    'E3',
    'E4',
    'E5',
    'E6',
]);
export const TaxTreatmentLabel = {
    S1: 'Sujeta y no exenta',
    S2: 'Sujeta, inversión del sujeto pasivo',
    N1: 'No sujeta (art. 7 y 14 LIVA)',
    N2: 'No sujeta por reglas de localización',
    E1: 'Exenta (art. 20 LIVA)',
    E2: 'Exenta: exportación (art. 21 LIVA)',
    E3: 'Exenta (art. 22 LIVA)',
    E4: 'Exenta (arts. 23 y 24 LIVA)',
    E5: 'Exenta: entrega intracomunitaria (art. 25 LIVA)',
    E6: 'Exenta por otros motivos',
};
export const InvoiceLineInputSchema = z.object({
    position: z.number().int().min(0).default(0),
    description: z.string().min(1).max(500),
    quantity: decimalString.default('1'),
    unitPriceCents: z.number().int(),
    discountPct: decimalString.default('0'),
    vatRate: decimalString.default('0.21'),
    isSupplied: z.boolean().default(false),
    /** VERI*FACTU: por qué va al 0 % (o S1 explícito). Nulo = el de siempre. */
    taxTreatment: TaxTreatmentSchema.nullable().optional(),
});
export const InvoiceLineDtoSchema = InvoiceLineInputSchema.extend({
    id: z.string(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
});
// ---------- Series ----------
export const InvoiceSeriesDtoSchema = z.object({
    id: z.string(),
    prefix: z.string(),
    name: z.string(),
    kind: InvoiceKindSchema,
    isDefault: z.boolean(),
    active: z.boolean(),
    issuerId: z.string(),
    issuerName: z.string(),
});
export const CreateInvoiceSeriesSchema = z.object({
    prefix: z.string().min(1).max(8),
    name: z.string().min(1).max(60),
    kind: InvoiceKindSchema.default('ORDINARIA'),
    isDefault: z.boolean().default(false),
    issuerId: z.string(),
});
// ---------- Pagos ----------
export const InvoicePaymentDtoSchema = z.object({
    id: z.string(),
    paidAt: z.string().datetime(),
    amountCents: z.number().int(),
    method: PaymentMethodSchema,
    reference: z.string().nullable(),
    notes: z.string().nullable(),
});
export const CreateInvoicePaymentSchema = z.object({
    paidAt: z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida'),
    amountCents: z.number().int().positive(),
    method: PaymentMethodSchema.default('TRANSFERENCIA'),
    reference: z.string().max(120).nullable().optional(),
    notes: z.string().max(500).nullable().optional(),
});
// ---------- Factura ----------
export const CreateInvoiceSchema = z.object({
    seriesId: z.string(),
    clientId: z.string(),
    caseId: z.string().nullable().optional(),
    kind: InvoiceKindSchema.default('ORDINARIA'),
    rectifiesId: z.string().nullable().optional(),
    rectificationReason: z.string().max(500).nullable().optional(),
    dueDate: z.string().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    internalNotes: z.string().max(2000).nullable().optional(),
    irpfRate: decimalString.optional(),
    lines: z.array(InvoiceLineInputSchema).default([]),
});
export const UpdateInvoiceSchema = CreateInvoiceSchema.partial().extend({
    fundsApplications: z
        .array(z.object({
        provisionId: z.string(),
        amountCents: z.number().int().positive(),
    }))
        .optional(),
});
export const EmitInvoiceSchema = z.object({
    issueDate: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida')
        .optional(),
});
export const InvoiceListItemSchema = z.object({
    id: z.string(),
    seriesId: z.string(),
    seriesPrefix: z.string(),
    issuerId: z.string(),
    issuerName: z.string(),
    number: z.string().nullable(),
    year: z.number().int().nullable(),
    issueDate: z.string().datetime().nullable(),
    dueDate: z.string().datetime().nullable(),
    status: InvoiceStatusSchema,
    kind: InvoiceKindSchema,
    clientId: z.string(),
    clientDisplayName: z.string(),
    caseId: z.string().nullable(),
    caseCode: z.string().nullable(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    irpfCents: z.number().int(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    /** B2: ya se pasó a la gestoría (null = pendiente de pasar). */
    accountedAt: z.string().datetime().nullable(),
});
export const InvoiceDetailSchema = InvoiceListItemSchema.extend({
    notes: z.string().nullable(),
    internalNotes: z.string().nullable(),
    rectifiesId: z.string().nullable(),
    rectifiesNumber: z.string().nullable(),
    rectificationReason: z.string().nullable(),
    rectificationType: RectificationTypeSchema.nullable(),
    rectifiedByNumbers: z.array(z.string()),
    irpfRate: z.string(),
    fundsAppliedCents: z.number().int(),
    discountCents: z.number().int(),
    previousHash: z.string().nullable(),
    currentHash: z.string().nullable(),
    csv: z.string().nullable(),
    verifactuQrUrl: z.string().nullable(),
    /** B1: la factura se expidió con registro VERI*FACTU (sin hash propio ni CSV propios). */
    verifactu: z.boolean().default(false),
    /** TipoFactura de la AEAT: F1, o R1-R4 en una rectificativa. */
    aeatInvoiceType: z.string().nullable().default(null),
    /** I (diferencias) o S (sustitución). */
    rectificationMethod: z.string().nullable().default(null),
    /** Suma de los suplidos (se imprimen aparte, fuera de la base y del desglose). */
    suppliedCents: z.number().int().default(0),
    /** Mención del art. 6.1.m RD 1619/2012 (exención, no sujeción, inversión del sujeto pasivo). */
    taxLegend: z.string().nullable().default(null),
    /** `ImporteTotal` registrado: bases + cuotas, sin suplidos ni retención (solo con VERI*FACTU). */
    importeTotalCents: z.number().int().nullable().default(null),
    /** En una sustitución (S): la base y la cuota de la factura que se sustituye. */
    rectifiedPrinted: z
        .object({ method: z.string(), baseCents: z.number().int(), taxCents: z.number().int() })
        .nullable()
        .default(null),
    voidedAt: z.string().datetime().nullable().default(null),
    voidReason: z.string().nullable().default(null),
    emittedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    issuerSnapshot: z.record(z.unknown()).nullable(),
    clientSnapshot: z.record(z.unknown()).nullable(),
    lines: z.array(InvoiceLineDtoSchema),
    payments: z.array(InvoicePaymentDtoSchema),
    vatBreakdown: z.array(z.object({ rate: z.number(), baseCents: z.number().int(), vatCents: z.number().int() })),
    appliedProvisions: z.array(z.object({
        provisionId: z.string(),
        amountCents: z.number().int(),
        reference: z.string().nullable(),
    })),
    /** T2: el asiento de la factura en el diario de su emisor (solo si lleva PGC y quien pregunta ve los libros). */
    ledgerEntry: z
        .object({ number: z.number().int().nullable(), date: z.string() })
        .nullable()
        .optional(),
});
export const InvoiceListQuerySchema = z.object({
    q: z.string().optional(),
    status: InvoiceStatusSchema.optional(),
    year: z.coerce.number().int().optional(),
    clientId: z.string().optional(),
    caseId: z.string().optional(),
    seriesId: z.string().optional(),
    issuerId: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const InvoiceListResponseSchema = z.object({
    items: z.array(InvoiceListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
// ---------- Provisiones ----------
export const FundsProvisionDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string(),
    caseId: z.string().nullable(),
    caseCode: z.string().nullable(),
    /** Emisor que recibió el dinero (T2: en su contabilidad PGC entra como anticipo de cliente). */
    issuerId: z.string().nullable(),
    issuerName: z.string().nullable(),
    /** `true` si el cobro de la provisión ya está en el diario del emisor. */
    ledgerBooked: z.boolean(),
    receivedAt: z.string().datetime(),
    amountCents: z.number().int(),
    appliedCents: z.number().int(),
    refundedCents: z.number().int(),
    availableCents: z.number().int(), // derivado: amount - applied - refunded
    status: FundsProvisionStatusSchema,
    method: PaymentMethodSchema,
    description: z.string().nullable(),
    reference: z.string().nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
});
export const CreateFundsProvisionSchema = z.object({
    clientId: z.string(),
    caseId: z.string().nullable().optional(),
    /** Emisor que recibe el dinero; si falta, el del expediente, el del cliente o el de por defecto. */
    issuerId: z.string().nullable().optional(),
    receivedAt: z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida'),
    amountCents: z.number().int().positive(),
    method: PaymentMethodSchema.default('TRANSFERENCIA'),
    description: z.string().max(500).nullable().optional(),
    reference: z.string().max(120).nullable().optional(),
});
export const UpdateFundsProvisionSchema = z.object({
    /** Solo mientras la provisión no tenga movimientos ni cobro contabilizado. */
    issuerId: z.string().nullable().optional(),
    receivedAt: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida')
        .optional(),
    method: PaymentMethodSchema.optional(),
    description: z.string().max(500).nullable().optional(),
    reference: z.string().max(120).nullable().optional(),
});
export const RefundFundsProvisionSchema = z.object({
    amountCents: z.number().int().positive(),
    notes: z.string().max(500).nullable().optional(),
});
export const FundsProvisionListResponseSchema = z.object({
    items: z.array(FundsProvisionDtoSchema),
    /** Saldo disponible total (sum availableCents). */
    availableTotalCents: z.number().int(),
});
// ---------- F19 Emisores (multi-NIF) ----------
/** Quién lleva la contabilidad de un emisor (D-B3). `PGC` no existe aún: necesita el núcleo `ctb`. */
export const AccountingRegimeSchema = z.enum(['NONE', 'IRPF_BOOKS', 'PGC']);
export const ACCOUNTING_REGIME_LABEL = {
    NONE: 'La gestoría lleva la contabilidad',
    IRPF_BOOKS: 'Libros del autónomo (art. 68 RIRPF)',
    PGC: 'Contabilidad por partida doble (PGC)',
};
export const IssuerDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    legalName: z.string().nullable(),
    nif: z.string(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    province: z.string().nullable(),
    country: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    bankIban: z.string().nullable(),
    bankBic: z.string().nullable(),
    invoiceFootnote: z.string().nullable(),
    active: z.boolean(),
    isDefault: z.boolean(),
    accountingRegime: AccountingRegimeSchema,
    /** NIF válido de persona física (puede llevar los libros del autónomo y los borradores 303/130). */
    naturalPerson: z.boolean(),
});
export const CreateIssuerSchema = z.object({
    name: z.string().min(2).max(200),
    legalName: z.string().max(200).nullable().optional(),
    nif: z.string().min(1).max(20),
    address: z.string().max(300).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    postalCode: z.string().max(10).nullable().optional(),
    province: z.string().max(100).nullable().optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    bankIban: z.string().max(40).nullable().optional(),
    bankBic: z.string().max(15).nullable().optional(),
    invoiceFootnote: z.string().max(1000).nullable().optional(),
    isDefault: z.boolean().default(false),
    accountingRegime: AccountingRegimeSchema.default('NONE'),
});
export const UpdateIssuerSchema = CreateIssuerSchema.partial().extend({
    active: z.boolean().optional(),
});
//# sourceMappingURL=billing.js.map